<template>
    <div v-if="viewingThreadBranch" class="draft-bar">
        <div class="draft-bar__info">
            <span class="draft-bar__title"
                >Draft &middot; {{ draftTitle }}</span
            >
            <button
                type="button"
                class="draft-bar__edit-details"
                :class="{
                    'draft-bar__edit-details--disabled': !isOwnDraft,
                }"
                :aria-disabled="!isOwnDraft"
                title="Edit draft details"
                aria-label="Edit draft details"
                v-tooltip.top="renameBlocked"
                data-testid="draft-bar-edit-details"
                @click="editDetails"
            >
                <i class="pi pi-pencil" />
            </button>
            <span v-if="metaLine" class="draft-bar__meta">{{ metaLine }}</span>
        </div>
        <div class="draft-bar__actions">
            <!-- Run has no thread drawer, so the conversation control is
                 absent there. Leave draft stays: that is how you get out. -->
            <button
                v-if="threadDrawerAvailable"
                type="button"
                class="draft-bar__action"
                :class="{
                    'draft-bar__action--active': threadsStore.drawerOpen,
                }"
                :disabled="!isReady"
                data-testid="draft-bar-thread"
                @click="toggleThread"
            >
                <!-- The chat glyph it had before the reframe: this opens the
                     conversation, which the word alone never made obvious. -->
                <i class="pi pi-comments" />
                {{ threadLabel }}
            </button>
            <!-- Throwing the draft away is its own action rather than a choice
                 offered while leaving, so it is reachable whether or not
                 anything has been edited — and so the button beside it can be
                 plainly safe. Needs an open draft, like Apply, and a reader
                 who may write — a viewer only looks. Unlike Apply, only the
                 author may discard (discarding a colleague's work outright is
                 not a call anyone else gets to make). A colleague sees it
                 disabled rather than removed, so the row keeps the shape it
                 had while loading. -->
            <button
                v-if="canResolve"
                type="button"
                class="draft-bar__action draft-bar__action--danger"
                :class="{
                    'draft-bar__action--not-allowed': isReady && !isOwnDraft,
                }"
                :aria-disabled="isReady && !isOwnDraft"
                v-tooltip.top="
                    isReady && !isOwnDraft
                        ? 'Only the author can discard this draft'
                        : undefined
                "
                :disabled="!isReady || isBusy"
                data-testid="draft-bar-discard"
                @click="discardDraft"
            >
                Discard draft
            </button>
            <!-- Always offered, and deliberately NOT gated on `isReady`: it is
                 the only way out of the draft view, so a draft whose record
                 never landed (resolved or removed in another session) would
                 otherwise strand the user here with every button dead. Held
                 down while the drain is in flight — it ends in a real request
                 or two, and a second click would race the first. -->
            <button
                type="button"
                class="draft-bar__action"
                :disabled="isBusy"
                data-testid="draft-bar-close"
                @click="closeDraft"
            >
                {{ busyWith === "leave" ? "Leaving…" : "Leave draft" }}
            </button>
            <!-- Applying needs an open draft; the backend refuses otherwise.
                 Shown while still loading so the row keeps its shape. -->
            <button
                v-if="canResolve"
                type="button"
                class="draft-bar__action draft-bar__action--accent"
                :disabled="!isReady || isBusy"
                data-testid="draft-bar-accept"
                @click="acceptDraft"
            >
                Apply draft
            </button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useDialog } from "primevue/usedialog";
import { computed, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

import EditDraftDetailsDialog from "@/components/Thread/EditDraftDetailsDialog.vue";
import ResolveThreadDialog from "@/components/Thread/ResolveThreadDialog.vue";
import { formatRelativeTime } from "@/components/Thread/thread-format";
import {
    isOwnOpenDraft,
    renameBlockedReason,
} from "@/components/Thread/thread-status";
import { useEditManager } from "@/composables/edit-manager";
import { useThreadBranchView } from "@/composables/use-thread-branch-view";
import { syncChannel } from "@/lib/broadcast-channels";
import { useReleaseStore } from "@/stores/release-store";
import { type ThreadSchema, useThreadsStore } from "@/stores/threads-store";
import { useUserManager } from "@/stores/user-store";

const route = useRoute();
const dialog = useDialog();
// The layout does not mount the thread drawer in live mode, so offering the
// button there would open nothing.
const threadDrawerAvailable = computed(
    () => !route.matched.some((record) => record.meta.mode === "live")
);
const threadsStore = useThreadsStore();
const releaseStore = useReleaseStore();
const { user } = useUserManager();
const { isViewOnly } = useEditManager();
const {
    viewingThreadBranch,
    branchThread,
    drainPendingEdits,
    leaveDraft,
    leaveIfResolved,
    goToReconcile,
} = useThreadBranchView();

// Don't wait on the whole list. `branchThread` resolves out of the threads
// store, which fills from fetchThreads — and list_threads recomputes
// affected_entities for EVERY open thread, a full worktree diff each. With a
// handful of open drafts that is seconds of the bar sitting disabled for want
// of one record. Fetch just this one instead; the list still lands later and
// takes over.
const ownThread = ref<ThreadSchema | undefined>();

// Fetched for every draft that becomes active, not only for the ones the list
// is missing. The list is the wrong source for the footprint — the drawer asks
// for it without diffs, so `affected_entities` is `[]` there whatever the
// branch holds — and `get_thread` recomputes it live. One request per draft
// activation, off the critical path: the bar renders from whichever record
// arrives first.
watch(
    () => releaseStore.activeThreadId,
    async (id) => {
        if (!id) {
            ownThread.value = undefined;
            return;
        }
        const fresh = await threadsStore.fetchThreadSummary(id);
        if (releaseStore.activeThreadId !== id) return;
        ownThread.value = fresh ?? undefined;
    },
    { immediate: true }
);

// Whichever arrived first.
const draft = computed(() => branchThread.value ?? ownThread.value);

// Off `draft`, so the title and the conflict check cover the window where the
// list has not landed yet: reading `branchThread` alone left the row under a
// placeholder title with live meta beneath it, and a draft behind main skipped
// the Reconcile redirect — Apply opened a dialog the backend then refused.
// fetchThreadSummary returns the whole record, conflicts included.
const draftTitle = computed(() => draft.value?.title ?? "Thread branch");
const pendingConflicts = computed(() =>
    (draft.value?.conflicts ?? []).filter((c) => c.status === "pending")
);

// Accepting and closing both need an OPEN draft — the backend refuses either
// with "Thread is already {status}". The id can outlive the open draft (a
// lingering ?thread= after a resolve, a reload on a stale URL, someone else
// resolving it in another session), so the actions key off the status rather
// than off merely having a draft in view.
const isOpen = computed(() => draft.value?.status === "open");

// The thread record arrives with fetchThreads, which is tied to stek load —
// so on a deep link or a reload the bar renders before it. Until then every
// handler here early-returns on the missing id, which showed up as a row of
// buttons that did nothing and then two more appearing once the fetch landed.
// Hold the actions down until we actually have the draft.
const isReady = computed(() => !!draft.value);
const isOwnDraft = computed(
    () => !!draft.value && isOwnOpenDraft(draft.value, user.value?.identity)
);
const renameBlocked = computed(() =>
    draft.value
        ? renameBlockedReason(draft.value, user.value?.identity)
        : undefined
);
// Apply and Discard write the draft, which the backend refuses a viewer.
const canResolve = computed(
    () => !isViewOnly.value && (!isReady.value || isOpen.value)
);

const editDetails = () => {
    if (!draft.value || !isOwnDraft.value) return;
    dialog.open(EditDraftDetailsDialog, {
        props: {
            header: "Draft details",
            modal: true,
            style: { width: "30rem" },
        },
        data: { thread: draft.value },
    });
};

const busyWith = ref<"leave" | "apply" | "discard" | null>(null);
const isBusy = computed(() => busyWith.value !== null);

// Whether this draft holds anything, said in words rather than as a count.
//
// A count cannot be told the truth cheaply. The number comes from a whole
// branch diff, so the drawer's listing now asks for the records WITHOUT it —
// which means the list's `affected_entities` is `[]` for drafts with plenty in
// them, and the bar used to render that as a confident "0 edits". Two honest
// sources instead:
//
//   ownThread   `get_thread` recomputes the footprint live, so this one is
//               authoritative — but only as of when it was read
//   editedHere  anything queued from this tab while this draft was active. It
//               cannot go stale and cannot under-report your own typing
//
// Either saying yes is enough. Neither having an answer yet says nothing at
// all, which is the one thing the count could not do.
const editedHere = ref(false);

watch(
    () => releaseStore.activeThreadId,
    () => {
        editedHere.value = false;
    }
);

// Its own receiver rather than the shared `syncChannel` object: a
// BroadcastChannel never delivers to the instance that posted, and the sync
// store posts on that very instance. Listening there meant this only ever
// lit up for edits made in ANOTHER tab — never the one being typed in, which
// is the only case it exists for. Same pattern as canvas-save-status.
const syncListener = new BroadcastChannel(syncChannel.name);
syncListener.addEventListener("message", ({ data: { message } }) => {
    if (message === "queued" && releaseStore.activeThreadId) {
        editedHere.value = true;
    }
});
onUnmounted(() => syncListener.close());

const changeLabel = computed(() => {
    if (editedHere.value) return "Edited";
    const footprint = ownThread.value?.affected_entities;
    if (!footprint) return "";
    return footprint.length > 0 ? "Edited" : "No changes yet";
});

// "Edited · started {relative}" — no author, since any collaborator with
// access to the draft can contribute edits to it, not just its creator.
// Degrades gracefully (either half missing) rather than showing a broken line.
const metaLine = computed(() => {
    const thread = draft.value;
    if (!thread) return "";
    const started = thread.created_at
        ? `started ${formatRelativeTime(thread.created_at)}`
        : "";
    return [changeLabel.value, started].filter(Boolean).join(" · ");
});

// This draft's own entry count, off the thread record. NOT activeEntries,
// which holds whichever thread was fetched last — so a fresh draft (created
// with a title and no description, hence no initial entry) would inherit the
// previous thread's count and promise a conversation that isn't there.
const threadLabel = computed(() => {
    const entryCount = draft.value?.entry_count ?? 0;
    return entryCount > 0 ? `Thread ${entryCount}` : "Thread";
});

// Show/hide the drawer for this draft's conversation. Purely manual — opening
// a draft (auto-create or navigating into one) never opens the drawer itself.
const toggleThread = () => {
    const id = draft.value?.identity;
    if (!id) return;
    if (threadsStore.drawerOpen && threadsStore.drawerThreadId === id) {
        threadsStore.closeDrawer();
    } else {
        threadsStore.openDrawer(id);
    }
};

// A behind-main draft blocks accepting until the conflicts are picked — send
// the user straight to reconcile instead of opening a dialog that would just
// fail, mirroring the drawer's own "Resolve N conflicts" CTA.
const acceptDraft = async () => {
    if (!isOpen.value || isBusy.value) return;
    busyWith.value = "apply";
    try {
        if (pendingConflicts.value.length > 0) {
            await goToReconcile();
            return;
        }
        const id = draft.value?.identity;
        if (!id) return;

        await drainPendingEdits();

        if (threadsStore.activeThread?.identity !== id) {
            await threadsStore.fetchThread(id);
        }
        dialog.open(ResolveThreadDialog, {
            props: {
                header: "Accept Thread",
                modal: true,
                style: { width: "450px" },
            },
            data: { action: "accept", threadId: id },
            onClose: () => leaveIfResolved(id),
        });
    } finally {
        busyWith.value = null;
    }
};

// Throwing the draft away. Its own deliberate action rather than a branch of
// leaving, so it is reachable whether or not anything has been edited — and so
// that Leave can be the plainly safe button beside it. Reuses the resolve
// dialog's reject side, which already asks for a reason and says what happens
// ("thrown away, nothing reaches your business, the discussion is kept").
const discardDraft = async () => {
    if (!isOwnDraft.value || isBusy.value) return;
    busyWith.value = "discard";
    try {
        const id = draft.value?.identity;
        if (!id) return;

        await drainPendingEdits();

        dialog.open(ResolveThreadDialog, {
            props: {
                header: "Discard draft",
                modal: true,
                style: { width: "450px" },
            },
            data: { action: "reject", threadId: id },
            onClose: () => leaveIfResolved(id),
        });
    } finally {
        busyWith.value = null;
    }
};

// Leaving keeps the draft. The one exception — an auto-created draft nothing
// went into — and the reason the browser does not decide it for itself both
// live on `leaveDraft`; see the note there.
//
// The drain inside it is what makes the decision safe, and clicking THIS
// button is why it has to be: the click blurs whatever field had focus, and
// some editors save on that blur after a delay (ObjectivesBoard waits 100ms).
// Without committing them first, the draft merely looks empty, the delete is
// accepted, and the save lands a tick later on a branch that is gone.
//
// `ownThread` is handed over for the window where the thread list has not
// landed and the bar's own single-thread fetch is the only record there is.
const closeDraft = async () => {
    if (isBusy.value) return;
    busyWith.value = "leave";
    try {
        await leaveDraft(ownThread.value);
    } finally {
        busyWith.value = null;
    }
};
</script>

<style scoped lang="scss">
.draft-bar {
    // The first row of the content column, above the menubar — so it reserves
    // its own space instead of the rest of the app allowing for it.
    flex: 0 0 auto;
    position: relative;
    z-index: 11;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    height: var(--draft-bar-height);
    padding: 0 1rem;
    // Same surface as NavRail — the other persistent, mode-colored chrome
    // element in the app.
    background: var(--stekz-primary-color);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    color: var(--text-color-primary-contrast, #fff);
    font-size: 0.85rem;

    &__info {
        display: flex;
        align-items: baseline;
        gap: 0.6rem;
        min-width: 0;
        overflow: hidden;
    }

    &__title {
        font-weight: 700;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__meta {
        opacity: 0.85;
        font-size: 0.8em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__edit-details {
        display: inline-flex;
        flex: 0 0 auto;
        align-items: center;
        justify-content: center;
        padding: 0.15rem;
        border: 0;
        background: transparent;
        color: inherit;
        cursor: pointer;
        font-size: 0.8rem;
        opacity: 0.75;

        .pi {
            font-size: 1em;
            line-height: 1;
        }

        &:hover,
        &:focus-visible {
            opacity: 1;
        }

        &--disabled {
            cursor: default;
            opacity: 0.35;

            &:hover,
            &:focus-visible {
                opacity: 0.35;
            }
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        // Right-aligned and non-shrinking, so the row keeps its shape while a
        // resolve is in flight. Discard and Apply unmount when the draft stops
        // being open — which happens mid-close, as the refetch lands — and
        // without this the remaining buttons slid sideways on the way out.
        justify-content: flex-end;
        gap: 0.5rem;
        flex-shrink: 0;
        min-width: min(28rem, 100%);
    }

    &__action {
        display: inline-flex;
        align-items: center;
        gap: 0.35em;
        background: none;
        border: 1px solid rgba(255, 255, 255, 0.7);
        color: var(--text-color-primary-contrast, #fff);
        cursor: pointer;
        font-size: 0.8rem;
        padding: 0.3em 0.75em;
        white-space: nowrap;

        // The icon inherits the button's font size and gets the text's line
        // box, so an icon button is exactly as tall as a text-only one.
        .pi {
            font-size: 1em;
            line-height: inherit;
        }

        &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.15);
        }

        &:disabled {
            opacity: 0.55;
            cursor: default;
        }

        // Destructive, so it is set apart from its neighbours — but only on
        // hover, since a permanently red button on a bar that is always on
        // screen reads as an alarm rather than an option.
        &--danger:hover {
            background: var(--p-red-500, #ef4444);
            border-color: var(--p-red-500, #ef4444);
        }

        &--not-allowed,
        &--not-allowed:hover {
            opacity: 0.55;
            cursor: default;
            background: none;
            border-color: rgba(255, 255, 255, 0.7);
        }

        // The one action that should stand out at a glance — inverted, so it
        // reads as the primary/default action on the bar.
        &--accent {
            background: var(--text-color-primary-contrast, #fff);
            color: var(--stekz-primary-color);
            border-color: transparent;
            font-weight: 600;

            &:hover {
                background: rgba(255, 255, 255, 0.9);
            }
        }

        &--active {
            background: rgba(255, 255, 255, 0.25);
        }
    }
}
</style>
